import platform from './platform';
import { ErrorCategories } from './errors';
import { getDeviceId, createCancellablePromise } from './utils';
import Logger from './logger';
import getViewableQuery from './getViewable.graphql';
import setAudioLangQuery from './setAudioLang.graphql';
import setSubtitleLangQuery from './setSubtitleLang.graphql';
import setSubtitlesEnabledQuery from './setSubtitlesEnabled.graphql';
import getPlaybackSettingsQuery from './getPlaybackSettings.graphql';

const config = {
  baseUri: '',
  authorizationKey: null,
  accessToken: null,
  entitlementToken: null,
};

export const changeBaseUri = (url) => {
  config.baseUri = url;
};

export const changeAuth = (authOptions) => {
  if (authOptions.accessToken !== undefined) {
    config.accessToken = authOptions.accessToken;
  }

  if (authOptions.authorizationKey !== undefined) {
    config.authorizationKey = authOptions.authorizationKey;
  }

  if (authOptions.entitlementToken !== undefined) {
    config.entitlementToken = authOptions.entitlementToken;
  }
};

function setRequestHeaders(req, headers, isAuthorized = false) {
  const usedRequestHeaders = [];
  const setRequestHeader = (requestOrigin, usedReqHeaders = usedRequestHeaders, ...header) => {
    if (requestOrigin.setRequestHeader) {
      const requestHeaderExists = !!usedReqHeaders.filter(key => key[0] === header[0]).length;
      if (requestHeaderExists) {
        return;
      }

      usedRequestHeaders.push(header);
      requestOrigin.setRequestHeader(...header);
    }
  };

  const addAuthorization = () => {
    setRequestHeader(req, usedRequestHeaders,
      'Authorization', `Bearer ${config.authorizationKey}`);
    setRequestHeader(req, usedRequestHeaders,
      'Magine-AccessToken', config.accessToken);
  };

  Object
    .keys(headers)
    .forEach(requestHeaderKey => setRequestHeader(req, usedRequestHeaders,
      requestHeaderKey, headers[requestHeaderKey]));

  if (isAuthorized) {
    addAuthorization();
  }

  return usedRequestHeaders;
}

export default function request(requestSettings = {}) {
  const {
    url = null,
    type = 'GET',
    payload = null,
    authorize = false,
    headers = {},
    responseType = 'text',
    session = '',
  } = requestSettings;

  let requestOpened;
  let requestCompleted;
  let requestTime;

  return new Promise((resolve, reject) => {
    if (!url) {
      Logger.warn('Request is missing url');

      reject({
        message: 'Missing a url, cannot Request without a valid url',
        code: 404,
        module: '/utils/request/Request',
        action: 'APP_EVENT',
        category: ErrorCategories.default,
      });
      return;
    }

    const xmlHTTPRequest = new XMLHttpRequest();
    xmlHTTPRequest.open(type, url, true);
    xmlHTTPRequest.responseType = responseType;
    xmlHTTPRequest.session = session;
    const usedHeaders = setRequestHeaders(xmlHTTPRequest, headers, authorize);

    xmlHTTPRequest.onload = () => {
      if (xmlHTTPRequest.status >= 200 && xmlHTTPRequest.status < 400) {
        requestCompleted = new Date() / 1000;
        requestTime = requestCompleted - requestOpened;
        xmlHTTPRequest.time = requestTime;

        let response = xmlHTTPRequest.response || xmlHTTPRequest.responseText;
        try {
          if (response.length) {
            response = JSON.parse(response);
          }
        } catch (ignore) {
          // Do nothing
        }

        resolve(response || {});
      } else {
        let statusCode = xmlHTTPRequest.status || 500;
        const response = xmlHTTPRequest.response || xmlHTTPRequest.responseText;
        if (xmlHTTPRequest.responseType === 'arraybuffer') {
          try {
            const decodedString = String.fromCharCode.apply(null, new Uint8Array(response));
            const drmError = JSON.parse(decodedString);
            statusCode = drmError.code;
          } catch (e) {
            statusCode = xmlHTTPRequest.status;
            Logger.warn('Cannot read response from arraybuffer');
          }
        }

        Logger.warn(
          'Request via', type,
          'to: ', url,
          'with headers: ', usedHeaders,
          'and payload: ', payload,
          'resulted in failure: ',
          xmlHTTPRequest,
        );

        reject({
          message: `Request for ${url} was denied`,
          code: statusCode,
          module: '/utils/request/request',
          action: 'APP_EVENT',
          category: ErrorCategories.network,
          extra: {
            response,
          },
        });
      }
    };

    xmlHTTPRequest.onerror = () => {
      Logger.warn(
        'Request via', type,
        'to: ', url,
        'with headers: ', usedHeaders,
        'and payload: ', payload,
        'resulted in failure: ',
        xmlHTTPRequest,
      );

      reject({
        message: `Could not request ${url}`,
        code: xmlHTTPRequest.status,
        module: '/utils/request/xmlHTTPRequest.onerror',
        action: 'APP_EVENT',
        category: ErrorCategories.network,
      });
    };

    requestOpened = new Date() / 1000;

    xmlHTTPRequest.send(payload || null);
  });
}

export function cancellableRequest(...args) {
  return createCancellablePromise((resolve, reject) => {
    request.call(null, ...args).then(resolve, reject);
  });
}

export function requestPreflight(options) {
  const {
    assetId,
    drm = 'widevine',
    protocol = 'dash',
    preflightPath = `/playback/v1/preflight/asset/${assetId}`,
  } = options;

  const preflightUrl = `${config.baseUri}${preflightPath}`;
  const deviceId = getDeviceId();
  const headers = {
    'Magine-Play-DeviceId': deviceId,
    'Magine-Play-DeviceModel': (platform.description || '').toLowerCase(),
    'Magine-Play-DevicePlatform': (platform.browser || '').toLowerCase(),
    'Magine-Play-DeviceType': 'web',
    // Request Widevine in case browser only supports ClearKey.
    // This enables playback test automation of non-DRM protected videos.
    'Magine-Play-DRM': drm === 'clearkey' ? 'widevine' : drm,
    // Use 'dashs' to get a 'https' url for the playlist in the response.
    // Prevents subsequent request to be blocked due to Mixed content.
    'Magine-Play-Protocol': protocol === 'dash' ? 'dashs' : protocol,
  };

  if (config.entitlementToken) {
    headers['Magine-Play-EntitlementId'] = config.entitlementToken;
  }

  return cancellableRequest({
    url: preflightUrl,
    type: 'POST',
    authorize: true,
    headers,
  });
}

export function requestEntitlement(assetId, pinCode) {
  const entitlementUrl = `${config.baseUri}/entitlement/v2/asset/${assetId}`;

  return request({
    url: entitlementUrl,
    type: 'POST',
    authorize: true,
    headers: { 'Content-Type': 'application/json' },
    payload: JSON.stringify({ pinCode }),
  });
}

export function requestAds(assetId) {
  const adsUrl = `${config.baseUri}/ads/v1/${assetId}`;

  return request({
    url: adsUrl,
    headers: {
      'Magine-Play-DeviceType': 'web',
      'Magine-Play-EntitlementId': config.entitlementToken,
    },
    type: 'GET',
    authorize: true,
    payload: JSON.stringify({ assetId }),
  });
}

export function requestCertificate(url, headers) {
  return request({
    url,
    type: 'GET',
    responseType: 'arraybuffer',
    authorize: true,
    headers,
  });
}

export function requestTelemetry(payload) {
  return request({
    url: `${config.baseUri}/tracker/v1/telemetry-events`,
    authorize: true,
    type: 'POST',
    payload: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function requestApiql(query, variables = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  return request({
    url: `${config.baseUri}/apiql/v2`,
    authorize: true,
    type: 'POST',
    headers,
    payload: JSON.stringify({ query, variables }),
  });
}

export function getViewable(viewableId, playableId) {
  return requestApiql(getViewableQuery, {
    viewableId,
    playableId,
  });
}

export function getPlaybackSettings() {
  return requestApiql(getPlaybackSettingsQuery).catch(() => ({}));
}

export function setSubtitleLang(subtitleLang) {
  return requestApiql(setSubtitleLangQuery, { subtitleLang }).catch(() => ({}));
}

export function setSubtitlesEnabled(subtitlesEnabled) {
  return requestApiql(setSubtitlesEnabledQuery, { subtitlesEnabled }).catch(() => ({}));
}

export function setAudioLang(audioLang) {
  return requestApiql(setAudioLangQuery, { audioLang }).catch(() => ({}));
}
